import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useSupabase } from "@/auth/SupabaseProvider";

import { apiFetch } from "./client";
import type { AgentRow, AgentsResponse } from "./types";

/**
 * TanStack Query hook for the agents registry — surfaces the friendly
 * `name` on `agents` so the SPA can render "DB Curator" in place of
 * raw `agent_type` (`db_curator`). The technical id stays available
 * via `title=` hover for forensic lookups.
 *
 * Returns the raw response array plus a memoised `agentsByType` lookup
 * map so consumers do `agentsByType[job.agentType]?.name ?? job.agentType`
 * without re-iterating on every render. Mirrors the shape of
 * `useCatalogue` / `useUsageSummary`; QueryClientProvider defaults
 * govern staleness — agents change rarely, but the cache is per-session
 * so a fresh navigation pulls a current copy.
 */
export function useAgents() {
  const { client } = useSupabase();
  const query = useQuery({
    queryKey: ["agents"],
    queryFn: ({ signal }) =>
      apiFetch<AgentsResponse>(client, "/api/agents", { signal }),
  });

  const agentsByType = useMemo(() => {
    const map: Record<string, AgentRow> = {};
    for (const row of query.data?.agents ?? []) {
      map[row.agentType] = row;
    }
    return map;
  }, [query.data]);

  return { ...query, agentsByType };
}

/**
 * Returns true when the given `agent_type` is the validator side of a
 * paired-agent run (curator + validator on the same job_id). The
 * convention is a `_validator` suffix on the type string — e.g.
 * `db_curator_validator` for the validator that pairs with
 * `db_curator`.
 *
 * Lives next to `useAgents` because it's a property of the type,
 * not the friendly display name. Surfaced as a helper so the rule
 * is in one place — string-match changes (e.g. a future M3 renaming
 * convention) only need to land here.
 */
export function isValidatorAgent(agentType: string): boolean {
  return agentType.endsWith("_validator");
}
